// ============================================================================
//  NarrativeLabs.js — labs attached to a specific narrative.
// ----------------------------------------------------------------------------
//  Labs are ALWAYS picked from the imported NHIS catalogue (gdrgs where
//  kind='investigation'). Free-typing a custom lab is not allowed — the
//  backend enforces the FK, and the UI only offers the catalogue picker.
//
//  Each lab carries the expected result (parameters + summary) tuned to the
//  narrative's clinical picture, either edited by hand or generated by AI.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  templateInvestigations,
  templateNarrativeLabs, templateNarrativeLabRow, templateNarrativeLabsSuggest,
} from '../../BaseURL';
import notify from './notify';
import { IconPlus, IconSparkle, IconEdit, IconTrash, IconSearch, IconClose } from './icons';

export default function NarrativeLabs({ narrativeId, narrativeLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(null);   // row being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(templateNarrativeLabs(narrativeId));
      setRows(data.rows || []);
    } catch (err) {
      notify.error('Failed to load labs', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [narrativeId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await notify.confirm(
      'Remove this lab?',
      `${row.investigation_code} · ${row.investigation_name} will be detached from this narrative.`,
      { confirmText: 'Remove', cancelText: 'Cancel', danger: true },
    );
    if (!ok) return;
    try {
      await axios.delete(templateNarrativeLabRow(narrativeId, row.id));
      notify.toast.success('Removed');
      load();
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.post(templateNarrativeLabsSuggest(narrativeId), { count: 5 });
      const msg = data.rejectedOutOfCatalogue
        ? `${data.inserted} added · ${data.rejectedOutOfCatalogue} rejected (outside catalogue)`
        : `${data.inserted} labs added to this narrative`;
      notify.toast.success(msg);
      load();
    } catch (err) {
      notify.error('AI suggest failed', err?.response?.data?.error || err.message);
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="tp-narr-labs">
      <div className="tp-narr-labs-head">
        <div className="tp-narr-labs-title">
          <span className="tp-narr-section-label">Labs Requested</span>
          <span className="tp-hint">Investigations picked from the NHIS booklet — matched to this narrative</span>
        </div>
        <div className="tp-narr-labs-actions">
          <button type="button" className="tp-btn tp-btn-ghost tp-btn-sm"
                  onClick={suggest} disabled={suggesting}>
            <IconSparkle size={14} />
            {suggesting ? 'Asking AI…' : 'Suggest labs with AI'}
          </button>
          <button type="button" className="tp-btn tp-btn-primary tp-btn-sm" onClick={() => setPicking(true)}>
            <IconPlus size={14} /> Add lab
          </button>
        </div>
      </div>

      {loading && <div className="tp-hint">Loading labs…</div>}

      {!loading && rows.length === 0 && (
        <div className="tp-narr-labs-empty">
          No labs attached yet. Press <strong>Suggest labs with AI</strong> to fill from the imported catalogue,
          or <strong>Add lab</strong> to pick one manually.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tp-narr-labs-list">
          {rows.map((r) => (
            <LabCard
              key={r.id}
              row={r}
              onEdit={() => setEditing(r)}
              onDelete={() => remove(r)}
            />
          ))}
        </div>
      )}

      {picking && (
        <LabPicker
          narrativeId={narrativeId}
          onClose={() => setPicking(false)}
          onAdded={() => { setPicking(false); load(); }}
        />
      )}

      {editing && (
        <LabResultEditor
          narrativeId={narrativeId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ── Single lab card ─────────────────────────────────────────────────── */
// Split visually into two panels:
//   • REQUEST — the lab that was ordered (code + name from the NHIS booklet)
//   • RESULT  — the parameter values + interpretation matching the narrative
function LabCard({ row, onEdit, onDelete }) {
  const params = parseParams(row.parameters_json);
  const hasResult = params.length > 0 || !!row.result_summary;
  return (
    <div className="tp-lab-card">
      {/* Request panel */}
      <div className="tp-lab-panel tp-lab-panel-request">
        <div className="tp-lab-panel-label">Request</div>
        <div className="tp-lab-request-body">
          <div className="tp-lab-code">{row.investigation_code}</div>
          <div className="tp-lab-name">{row.investigation_name}</div>
        </div>
        <div className="tp-lab-actions" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tp-icon-btn" onClick={onEdit} title="Edit result">
            <IconEdit size={14} />
          </button>
          <button type="button" className="tp-icon-btn danger" onClick={onDelete} title="Remove lab">
            <IconTrash size={14} />
          </button>
        </div>
      </div>

      {/* Result panel */}
      <div className="tp-lab-panel tp-lab-panel-result">
        <div className="tp-lab-panel-label">Result</div>
        {!hasResult && (
          <div className="tp-hint" style={{ padding: '4px 0' }}>
            No result yet — press <strong>Edit</strong> to enter one, or delete and re-generate with AI.
          </div>
        )}

        {params.length > 0 && (
          <table className="tp-lab-params">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Result</th>
                <th>Unit</th>
                <th>Reference</th>
                <th>Flag</th>
              </tr>
            </thead>
            <tbody>
              {params.map((p, i) => (
                <tr key={i}>
                  <td>{p.name}</td>
                  <td><strong>{p.value}</strong></td>
                  <td>{p.unit || ''}</td>
                  <td>{p.ref_range || ''}</td>
                  <td>
                    {p.flag ? (
                      <span className={`tp-lab-flag tp-lab-flag-${(p.flag || '').toLowerCase()}`}>
                        {p.flag}
                      </span>
                    ) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {row.result_summary && (
          <div className="tp-lab-summary">
            <span className="tp-lab-summary-label">Interpretation</span>
            <span>{row.result_summary}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function parseParams(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch { return []; }
}

/* ── Lab picker modal — searches the imported INVE catalogue ─────────── */
function LabPicker({ narrativeId, onClose, onAdded }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(templateInvestigations, { params: { q } });
        setRows(data.rows || []);
      } catch (err) {
        notify.toast.error('Search failed');
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const add = async (r) => {
    setBusyId(r.id);
    try {
      await axios.post(templateNarrativeLabs(narrativeId), { investigationGdrgId: r.id });
      notify.toast.success(`${r.code} added — edit to set the result`);
      onAdded?.();
    } catch (err) {
      notify.error('Add failed', err?.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>Add a lab</h3>
            <p className="tp-modal-sub">Only investigations from the imported NHIS booklet can be attached.</p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <div className="tp-editor-body" style={{ padding: '16px 24px' }}>
          <div className="tp-source-search">
            <IconSearch size={14} />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code or lab name (e.g. Malaria, FBC, INVE01)…"
              autoFocus />
          </div>

          <div className="tp-source-list tp-source-list-tall" style={{ marginTop: 12 }}>
            {loading && <div className="tp-source-empty">Searching…</div>}
            {!loading && rows.length === 0 && (
              <div className="tp-source-empty">
                No matches. {q ? 'Try a broader term.' : 'Type to search.'}
              </div>
            )}
            {!loading && rows.map((r) => (
              <button
                key={r.id}
                type="button"
                className="tp-source-row"
                disabled={busyId === r.id}
                onClick={() => add(r)}
              >
                <span className="tp-source-code">{r.code}</span>
                <span className="tp-source-name">{r.name}</span>
                <span className="tp-source-tags">
                  <span className="tp-chip">{r.mdc_code}</span>
                </span>
                <span className="tp-source-choose">Add</span>
              </button>
            ))}
          </div>
        </div>

        <div className="tp-modal-actions">
          <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit a lab's result (parameters + summary) ──────────────────────── */
function LabResultEditor({ narrativeId, row, onClose, onSaved }) {
  const [summary, setSummary] = useState(row.result_summary || '');
  const [params, setParams] = useState(() => parseParams(row.parameters_json));
  const [saving, setSaving] = useState(false);

  const patchParam = (i, key, val) => {
    setParams((p) => p.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  };
  const removeParam = (i) => setParams((p) => p.filter((_, idx) => idx !== i));
  const addParam = () => setParams((p) => [...p, { name: '', value: '', unit: '', ref_range: '', flag: '' }]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanParams = params
        .map((x) => ({
          name:      String(x.name || '').trim(),
          value:     String(x.value || '').trim(),
          unit:      String(x.unit || '').trim(),
          ref_range: String(x.ref_range || '').trim(),
          flag:      String(x.flag || '').trim(),
        }))
        .filter((x) => x.name);
      await axios.put(templateNarrativeLabRow(narrativeId, row.id), {
        resultSummary: summary,
        parameters: cleanParams,
      });
      notify.toast.success('Result updated');
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>Edit lab result</h3>
            <p className="tp-modal-sub">
              {row.investigation_code} · {row.investigation_name}
            </p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <form onSubmit={save} className="tp-editor-body" style={{ padding: '16px 24px' }}>
          <div className="tp-field">
            <label>Parameters</label>
            <table className="tp-lab-params tp-lab-params-edit">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Value</th>
                  <th>Unit</th>
                  <th>Reference</th>
                  <th>Flag</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {params.map((p, i) => (
                  <tr key={i}>
                    <td><input className="tp-input" value={p.name || ''}
                      onChange={(e) => patchParam(i, 'name', e.target.value)} placeholder="Hb" /></td>
                    <td><input className="tp-input" value={p.value || ''}
                      onChange={(e) => patchParam(i, 'value', e.target.value)} placeholder="8.5" /></td>
                    <td><input className="tp-input" value={p.unit || ''}
                      onChange={(e) => patchParam(i, 'unit', e.target.value)} placeholder="g/dL" /></td>
                    <td><input className="tp-input" value={p.ref_range || ''}
                      onChange={(e) => patchParam(i, 'ref_range', e.target.value)} placeholder="12–16" /></td>
                    <td>
                      <select className="tp-input" value={p.flag || ''}
                        onChange={(e) => patchParam(i, 'flag', e.target.value)}>
                        <option value=""></option>
                        <option value="LOW">LOW</option>
                        <option value="NORMAL">NORMAL</option>
                        <option value="HIGH">HIGH</option>
                      </select>
                    </td>
                    <td>
                      <button type="button" className="tp-icon-btn danger" onClick={() => removeParam(i)}>
                        <IconTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="tp-btn tp-btn-ghost tp-btn-sm" onClick={addParam} style={{ marginTop: 8 }}>
              <IconPlus size={12} /> Add parameter
            </button>
          </div>

          <div className="tp-field">
            <label>Interpretation / summary</label>
            <textarea className="tp-input" rows={3} value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Overall interpretation of the result in 1–2 sentences" />
          </div>

          <div className="tp-modal-actions">
            <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save result'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
